import React from 'react';
import { useIdentity } from '../identity/IdentityProvider';

/**
 * Gives a class component the tenant's identity as an `identity` prop.
 *
 * `useIdentity()` covers the function components, but several screens here are
 * still classes - the payment flow and both maps - and a class cannot call a
 * hook. This is app wiring rather than part of the shared identity module,
 * which is copied verbatim between frontends and stays framework-plain.
 *
 * Wrap innermost, so the component keeps receiving whatever props its existing
 * decorators (withRouter, withGoogleMap) already pass it:
 *
 *     export default withScriptjs(withGoogleMap(withIdentity(Map)));
 */
export function withIdentity<P extends object>(
  Component: React.ComponentType<P & { identity: any }>
): React.FC<P> {
  const WithIdentity: React.FC<P> = (props: P) => {
    const identity = useIdentity();
    return <Component {...props} identity={identity} />;
  };

  WithIdentity.displayName = `withIdentity(${
    Component.displayName || Component.name || 'Component'
  })`;

  return WithIdentity;
}

export default withIdentity;
